import React from "react";
import dynamic from "next/dynamic";

import { useRouter } from "next/router";
import { Formik, Field, Form } from "formik";
import { object, string, number } from "yup";
import { FormattedNumberInput, Input } from "../components/Input";
import Button from "../components/Button";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Checkbox,
  FormHelperText,
  Text,
  HStack,
  Wrap,
  WrapItem,
  Flex,
} from "@chakra-ui/react";
import Loader from "../components/Loader";
import useDonation from "../hooks/useDonations";
import {
  formatMoney,
  getMaxTip,
  getMinTip,
  getTiktokVideoId,
  shortHandNumber,
  VENDOR_CUT,
  VENDOR_CUT_RATES,
  getInstagramVideoId,
  getMinTipIDR,
  getMaxTipIDR,
  getMinTipPHP,
  getMaxTipPHP,
  getMinTipJPY,
  getMaxTipJPY,
} from "../utils/";
import { Large, P } from "../components/Type";
import Link from "../components/Link";
import { VoteSelector } from "./StreamerPage/VoteSelector";
import { MediaSelector } from "./StreamerPage/MediaSelector";
import { MabarSelector } from "./StreamerPage/MabarSelector";
import { PriorityAlertSelector } from "./StreamerPage/PriorityAlertSelector";
import { ChallengeSelector } from "./StreamerPage/ChallengeSelector";
import getVideoId from "get-video-id";
import { UsernameBadge } from "../components/Username";
import MinAmountInfo from "../components/MinAmountInfo";
import isEmail from "validator/lib/isEmail";
import { Trans, msg, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { PaymentMethods } from "./PaymentMethods";
import { getFormError } from "../i18n";
import {
  PaymentMethodSelectorProvider,
  usePaymentMethodSelector,
} from "../contexts/paymentMethodSelector";
import { FaBell } from "react-icons/fa";
import { PiSpeakerSimpleHighFill } from "react-icons/pi";
import useCheckReward from "../hooks/useCheckReward";
import { get } from "ky-universal";

const GiphyContextProvider = dynamic(
  () => {
    return import("../contexts/giphy").then((mod) => {
      return mod.GiphyContextProvider;
    });
  },
  { ssr: false }
);

const baseYupModel = {
  from: string()
    .required(msg`Nama tidak boleh kosong`)
    .max(32, msg`Nama terlalu panjang`)
    .test(
      "not-readable-domain",
      (message) => msg`Nama tidak diperbolehkan`,
      async (value = "") => {
        return await !isReadableAsDomain(value);
      }
    ),

  message: string()
    .required(msg`Pesan tidak boleh kosong`)
    .max(140),
  ytStart: number().moreThan(-1, msg`Tidak boleh negatif`),
  email: string()
    .required(msg`Masukkan email kamu`)
    .email(msg`Masukan email kamu`)
    .test(
      "is-valid",
      (message) => msg`Periksa lagi email kamu`,
      async (value = "") => {
        return await isEmail(value);
      }
    ),
  phone: string(),
};

const getGatewayTipRange = (currency, gateway) => {
  switch (currency) {
    case "PHP":
      return { min: getMinTipPHP(gateway), max: getMaxTipPHP(gateway) };
    case "JPY":
      return { min: getMinTipJPY(gateway), max: getMaxTipJPY(gateway) };
    case "IDR":
    default:
      return { min: getMinTipIDR(gateway), max: getMaxTipIDR(gateway) };
  }
};

const isAmountWithinGatewayRange = ({ currency, gateway, amount, transactionFeePolicy }) => {

  if (!gateway) return true; // belum pilih payment method
  const n = Number(amount);
  if (!Number.isFinite(n)) return false;

  const { min, max } = getGatewayTipRange(currency, gateway);
  const rate = VENDOR_CUT_RATES[gateway];
  if (transactionFeePolicy === "TIPPER" && Array.isArray(rate)) {
    return n >= min && n <= (max - rate[1]);
  }
  return n >= min && n <= max;
};

// The form field holding each media type's value; media is only sent when
// both the type and its value are set (see createDonation).
const MEDIA_VALUE_FIELD = {
  gif: "giphy",
  yt: "yt",
  tiktok: "tiktok",
  reels: "reels",
  voice_note: "voice_note",
  sb: "soundboard",
  image_guess: "image_guess",
};

// Minimum for the selected content to be shown, which a priority donation must
// reach too (same rule as the backend's _get_priority_content_min).
const getPriorityContentMinimum = (overlay, plus, values) => {
  const { mediaType } = values;
  const media = mediaType && values[MEDIA_VALUE_FIELD[mediaType]] ? mediaType : null;

  if (media === "sb") {
    // the soundboard item price is checked with the soundboard
    return null;
  }
  if (media === "image_guess") {
    return { label: "Min. tebak gambar:", amount: Number(overlay.min_image_guess) || 0 };
  }

  // text, gif and mediashare alerts are dropped below overlay.min (Plus only)
  const alertMin = plus ? Number(overlay.min) || 0 : 0;
  if (["yt", "tiktok", "reels"].includes(media)) {
    return {
      label: "Min. media share:",
      amount: Math.max(alertMin, Number(overlay.min_media) || 0),
    };
  }
  if (media === "voice_note") {
    return {
      label: "Min. voice note:",
      amount: Math.max(alertMin, Number(overlay.min_sound) || 0),
    };
  }
  return { label: "Min. muncul alert:", amount: alertMin };
};

// Priority alert: the tipper pays priority_alert_price on top of the amount so
// the alert is played before normal alerts. Shown when the streamer enables it,
// selectable once the amount reaches min_priority_alert and the selected
// content's own minimum.
const getPriorityAlert = (overlay, plus, values) => {
  const amount = Number(values.amount);
  const price = Number(overlay.priority_alert_price) || 0;
  const isEnabled = Boolean(overlay.priority_alert);
  const isFilled = values.amount !== "" && Number.isFinite(amount);

  const minimums = [
    { label: "Min. Prioritas:", amount: Number(overlay.min_priority_alert) || 0 },
  ];
  const contentMinimum = getPriorityContentMinimum(overlay, plus, values);
  if (contentMinimum && contentMinimum.amount > minimums[0].amount) {
    minimums.push(contentMinimum);
  }
  // minimums the amount doesn't reach yet (all of them while it's empty)
  const unmetMinimums = minimums.filter((m) => !isFilled || amount < m.amount);

  // like mabar / media: clickable until an amount below a minimum is typed
  const isAvailable = isEnabled && (!isFilled || !unmetMinimums.length);
  const isSelected = isAvailable && Boolean(values.priority_alert);

  return {
    isEnabled,
    isFilled,
    isAvailable,
    isSelected,
    price,
    unmetMinimums,
    // jumlah dukungan = amount + priority alert price
    totalAmount: isSelected ? amount + price : amount,
  };
};

// Challenge: the tipper pays challenge.price on top of the amount. Selectable
// once the amount (without the price) reaches min_donate, and not combined with
// mabar or any media except GIF (backend: CHALLENGE_CLOSED).
const getChallenge = (challenges = [], values) => {
  const amount = Number(values.amount);
  const isFilled = values.amount !== "" && Number.isFinite(amount);
  const isBlocked =
    Boolean(values.mabar_type) ||
    Boolean(values.mediaType && values.mediaType !== "gif");

  const items = challenges.map((c) => {
    const isAvailable = !isBlocked && (!isFilled || amount >= Number(c.min_donate));
    return { ...c, isAvailable, isSelected: isAvailable && c.slug === values.challenge_slug };
  });
  const selected = items.find((c) => c.isSelected) || null;

  return {
    items,
    isFilled,
    selected,
    price: selected ? Number(selected.price) || 0 : 0,
    unmetMinimums: isFilled ? challenges.filter((c) => amount < Number(c.min_donate)) : [],
  };
};

const getYupSchema = (currency) => {
  const MINIMUM_TIP_AMOUNT = getMinTip(currency);
  const MAXIMUM_TIP_AMOUNT = getMaxTip(currency);

  return object().shape({
    ...baseYupModel,
    amount: number()
      .required(msg`Masukkan nominal yang ingin diberikan`)
      .min(
        MINIMUM_TIP_AMOUNT,
        msg`Nominal minimum ${formatMoney(MINIMUM_TIP_AMOUNT, {
          currency,
        })}`
      )
      .max(
        MAXIMUM_TIP_AMOUNT,
        msg`Nominal maksimum ${formatMoney(MAXIMUM_TIP_AMOUNT, {
          currency,
        })}`
      ),
  });
};

function isReadableAsDomain(str) {
  // Remove all white spaces
  var noSpaces = str.replace(/\s/g, "");

  if (noSpaces.indexOf("http") >= 0 || noSpaces.indexOf(":/") >= 0) {
    return true;
  }

  // Use regex to check for domain name format
  var domainRegex = /^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}$/;

  return domainRegex.test(noSpaces.toLowerCase());
}

const phoneRegex = new RegExp("^08\\d{7,11}$");

const quickAmountColors = [
  "saweria-green.normal",
  "saweria-blue.normal",
  "saweria-yellow.normal",
  "saweria-purple.normal",
  "saweria-pink.normal",
];

const DonationForm = ({
  plus,
  overlay,
  vote,
  streamerId,
  isGopayEnabled,
  isOvoEnabled,
  isDanaEnabled,
  isLinkAjaEnabled,
  isQRISEnabled,
  isBCAVAEnabled,
  isMandiriVAEnabled,
  isBRIVAEnabled,
  isBNIVAEnabled,
  isPermataVAEnabled,
  isBJBVAEnabled,
  isBSIVAEnabled,
  isCIMBVAEnabled,
  currUsername = "",
  currEmail = "",
  appliedFee = false,
  currency,
  transactionFeePolicy = null,
  mabar,
  challenge: challenges = [],
}) => {
  const { createDonation, calculateAmountToPay } = useDonation();
  const { checkReward } = useCheckReward();

  const [showRewardNotification, setShowRewardNotification] = React.useState(false);
  const donateBtnRef = React.useRef(null);
  const [isRewardHidden, setIsRewardHidden] = React.useState(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsRewardHidden(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );

    if (donateBtnRef.current) observer.observe(donateBtnRef.current);

    return () => observer.disconnect();
  }, [showRewardNotification]);

  const yupSchema = getYupSchema(currency);
  const {
    min: minimumAlert,
    tts: tts,
    min_tts: minTTS,
    quick_amounts: quickAmounts,
  } = overlay;
  const { _ } = useLingui();

  return (
    <GiphyContextProvider>
      <PaymentMethodSelectorProvider>
        <Formik
          validationSchema={yupSchema}
          initialValues={{
            agree: false,
            notUnderage: false,
            from: currUsername,
            message: "",
            amount: "",
            payment_type: "",
            email: currEmail,
            phone: "",
            vote: "",
            giphy: null,
            yt: "",
            ytStart: 0,
            mediaType: null,
            image_guess: null,
            image_guess_answer: "",
            amountToPay: "",
            currency,
            pgFee: "",
            platformFee: "",
            mabar_type: null,
            mabar_game_id: "",
            mabar_custom_fields_1: "",
            mabar_custom_fields_2: "",
            priority_alert: false,
            challenge_slug: "",
          }}
          enableReinitialize
          validate={async (val) => {
            const error = {};

            if (val.payment_type === "ovo") {
              if (!phoneRegex.test(val.phone)) {
                error["phone"] = msg`Format nomor telepon salah. Gunakan nomor lainnya`;
              }
              if (!val.phone) {
                error[
                  "phone"
                ] = msg`Masukkan nomor telepon untuk pembayaran ${val.payment_type.toUpperCase()}`;
              }
            }
            if (val.payment_type === "qris") {

              if (val.amountToPay > 10_000_000) {
                error[
                  "payment_type"
                ] = msg`Maksimal total pembayaran menggunakan QRIS adalah Rp10.000.000`;
              }
            }

            if (val.payment_type === "bca") {
              if (val.amountToPay < 10_000) {
                error[
                  "payment_type"
                ] = msg`Minimum total pembayaran menggunakan Virtual Account BCA adalah Rp10.000`;
              }

              if (val.amountToPay > 100_000_000) {
                error[
                  "payment_type"
                ] = msg`Maksimal total pembayaran menggunakan Virtual Account BCA adalah Rp100.000.000`;
              }
            }

            if (val.payment_type === "mandiri") {
              if (val.amountToPay < 1_000) {
                error[
                  "payment_type"
                ] = msg`Minimum total pembayaran menggunakan Virtual Account Mandiri adalah Rp1.000`;
              }

              if (val.amountToPay > 30_000_000) {
                error[
                  "payment_type"
                ] = msg`Maksimal total pembayaran menggunakan Virtual Account Mandiri adalah Rp30.000.000`;
              }
            }

            if (val.payment_type === "bni") {
              if (val.amountToPay < 1_000) {
                error[
                  "payment_type"
                ] = msg`Minimum total pembayaran menggunakan Virtual Account BNI adalah Rp1.000`;
              }

              if (val.amountToPay > 50_000_000) {
                error[
                  "payment_type"
                ] = msg`Maksimal total pembayaran menggunakan Virtual Account BNI adalah Rp50.000.000`;
              }
            }

            if (val.payment_type === "bri") {
              if (val.amountToPay < 1_000) {
                error[
                  "payment_type"
                ] = msg`Minimum total pembayaran menggunakan Virtual Account BRI adalah Rp1.000`;
              }

              if (val.amountToPay > 100_000_000) {
                error[
                  "payment_type"
                ] = msg`Maksimal total pembayaran menggunakan Virtual Account BRI adalah Rp100.000.000`;
              }
            }

            if (val.payment_type === "permata") {
              if (val.amountToPay < 1_000) {
                error[
                  "payment_type"
                ] = msg`Minimum total pembayaran menggunakan Virtual Account Permata adalah Rp1.000`;
              }

              if (val.amountToPay > 100_000_000) {
                error[
                  "payment_type"
                ] = msg`Maksimal total pembayaran menggunakan Virtual Account Permata adalah Rp100.000.000`;
              }
            }
            if (val.payment_type === "bjb") {
              if (val.amountToPay < 1_000) {
                error[
                  "payment_type"
                ] = msg`Minimum total pembayaran menggunakan Virtual Account BJB adalah Rp1.000`;
              }

              if (val.amountToPay > 100_000_000) {
                error[
                  "payment_type"
                ] = msg`Maksimal total pembayaran menggunakan Virtual Account BJB adalah Rp100.000.000`;
              }
            }
            if (val.payment_type === "bsi") {
              if (val.amountToPay < 1_000) {
                error[
                  "payment_type"
                ] = msg`Minimum total pembayaran menggunakan Virtual Account BSI adalah Rp1.000`;
              }

              if (val.amountToPay > 100_000_000) {
                error[
                  "payment_type"
                ] = msg`Maksimal total pembayaran menggunakan Virtual Account BSI adalah Rp100.000.000`;
              }
            }
            if (val.payment_type === "cimb") {
              if (val.amountToPay < 1_000) {
                error[
                  "payment_type"
                ] = msg`Minimum total pembayaran menggunakan Virtual Account CIMB adalah Rp1.000`;
              }

              if (val.amountToPay > 100_000_000) {
                error[
                  "payment_type"
                ] = msg`Maksimal total pembayaran menggunakan Virtual Account CIMB adalah Rp100.000.000`;
              }
            }

            if (!val.notUnderage) {
              error[
                "notUnderage"
              ] = msg`Anda harus berusia 17 tahun atau lebih.`;
            }
            if (!val.agree) {
              error[
                "agree"
              ] = msg`Anda harus menyetujui syarat dan ketentuan saweria.co`;
            }
            if (val.mediaType === "yt") {
              const { yt } = val;
              const { id, service } = getVideoId(yt);
              if (service !== "youtube" || !id) {
                error["yt"] = msg`Cek ulang URL youtube diatas`;
              }
            }
            if (val.mediaType === "tiktok") {
              const { tiktok } = val;
              const { id, service } = await getTiktokVideoId(tiktok);
              if (service !== "tiktok" || !id) {
                error["tiktok"] = msg`Cek ulang URL tiktok diatas`;
              }
            }
            if (val.mediaType === "reels") {
              const { reels } = val;
              const id = getInstagramVideoId(reels);
              if (!id) {
                error["reels"] = msg`Cek ulang URL reels diatas`;
              }
            }
            if (val.mediaType === "image_guess") {
              if (!val.image_guess) {
                error["image_guess"] = msg`Upload gambar terlebih dahulu`;
              }
              if (!val.image_guess_answer || val.image_guess_answer.trim() === "") {
                error["image_guess_answer"] = msg`Jawaban tidak boleh kosong`;
              }
            }

            if (val.mabar_type) {
              const mabarFields = mabar?.custom_fields?.fields || [];
              const keysToValidate = ["mabar_game_id"];
              mabarFields.forEach((_, i) => keysToValidate.push(`mabar_custom_fields_${i + 1}`));
              for (const key of keysToValidate) {
                const v = (val[key] || "").trim();
                if (v.length < 1) {
                  error[key] = msg`Min. 1 karakter`;
                }
              }
            }

            return error;
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const { amountToPay, pgFee, platformFee, mabar_type, mabar_game_id, mabar_custom_fields_1, mabar_custom_fields_2, priority_alert, challenge_slug, ...valueRes } = values;

              // send the original amount; backend adds the priority alert price
              if (getPriorityAlert(overlay, plus, values).isSelected) {
                valueRes.priority_alert = true;
              }
              // amount stays the donation only; backend adds the challenge price
              const { selected: selectedChallenge } = getChallenge(challenges, values);
              if (selectedChallenge) {
                valueRes.challenge_slug = selectedChallenge.slug;
              }

              // Build mabar payload if type selected
              if (mabar_type) {
                const mabarFields = mabar?.custom_fields?.fields || [];
                const customFieldsPayload = {};
                const formKeys = [mabar_custom_fields_1, mabar_custom_fields_2];
                mabarFields.forEach((f, i) => {
                  if (f?.key && formKeys[i]) {
                    customFieldsPayload[f.key] = formKeys[i];
                  }
                });
                valueRes.mabar = {
                  mabar_type,
                  game_id: mabar_game_id,
                  custom_fields: Object.keys(customFieldsPayload).length > 0 ? customFieldsPayload : undefined,
                };
              }

              await createDonation(streamerId, valueRes);
            } catch (error) {
              console.log(error);
            }
            setSubmitting(false);
          }}
        >
          {({
            setFieldValue,
            setValues,
            submitForm,
            isSubmitting,
            values,
            setFieldError,
            errors,
            touched,
          }) => {
            // const { _ } = useLingui();
            const router = useRouter();
            const {
              selectedPaymentMethod,
              setSelectedPaymentMethod,
              setTransactionFeePolicy,
              setTransactionAmount,
              setPgFee,
            } = usePaymentMethodSelector();
            const priorityAlert = getPriorityAlert(overlay, plus, values);
            const challenge = getChallenge(challenges, values);
            // jumlah dukungan = amount + priority alert price + challenge price
            const totalAmount = priorityAlert.totalAmount + challenge.price;

            // the total changes when priority or the challenge stops (or starts)
            // applying, e.g. after picking a video below its minimum: redo the
            // payment preview
            const extrasKey = `${priorityAlert.isSelected}|${challenge.selected?.slug || ""}`;
            const prevExtrasKey = React.useRef(extrasKey);
            React.useEffect(() => {
              if (prevExtrasKey.current === extrasKey) {
                return;
              }
              prevExtrasKey.current = extrasKey;
              setSelectedPaymentMethod();
              setFieldValue("payment_type", "");
              setFieldValue("amountToPay", "");
              setFieldValue("pgFee", "");
              setFieldValue("platformFee", "");
            }, [extrasKey]);

            setTransactionFeePolicy(transactionFeePolicy);
            setTransactionAmount(
              values.amount === "" ? values.amount : totalAmount
            );
            setPgFee(values.pgFee);

            const onClickPaymentMethod = (type) => async () => {
              const { min, max } = getGatewayTipRange(currency, type);
              const amountValue = totalAmount;

              if (!Number.isFinite(amountValue) || amountValue < min || amountValue > max) {
                setFieldError(
                  "payment_type",
                  msg`Nominal harus di antara ${formatMoney(min, {
                    currency,
                  })} - ${formatMoney(max, { currency })} untuk metode pembayaran ini`
                );
                return;
              }

              setFieldError("payment_type", undefined);
              setSelectedPaymentMethod(type);
              await setFieldValue("payment_type", type, false);

              const { username } = router.query;
              const { priority_alert, challenge_slug, ...calcValues } = values;
              const amountToPayRes = await calculateAmountToPay(username, {
                ...calcValues,
                amount: totalAmount,
                payment_type: type,
              });

              await setFieldValue("amountToPay", amountToPayRes.amount_to_pay);
              await setFieldValue("pgFee", amountToPayRes.pg_fee);
              await setFieldValue("platformFee", amountToPayRes.platform_fee);
            };

            const chooseVote = (vote) => {
              setFieldValue("vote", vote);
            };

            if (isSubmitting) {
              return <Loader />;
            }

            const amountNumber = Number(values.amount);
            const isAmountFilled = values.amount !== "" && Number.isFinite(amountNumber);

            const isCoreFormFilled =
              isAmountFilled &&
              Boolean(values.from?.trim()) &&
              Boolean(values.message?.trim()) &&
              Boolean(values.email?.trim()) &&
              Boolean(values.notUnderage) &&
              Boolean(values.agree);

            const shouldFilterByGatewayRange = isCoreFormFilled;

            const isGatewayOutOfRange = (gateway) =>
              shouldFilterByGatewayRange &&
              !isAmountWithinGatewayRange({
                currency,
                gateway,
                amount: totalAmount,
                transactionFeePolicy,
              });

            // Disable map per gateway (JANGAN dipakai untuk hide)
            const disabledPayments = {
              // IDR
              qris: isGatewayOutOfRange("qris"),
              gopay: isGatewayOutOfRange("gopay"),
              ovo: isGatewayOutOfRange("ovo"),
              dana: isGatewayOutOfRange("dana"),
              linkaja: isGatewayOutOfRange("linkaja"),
              bca: isGatewayOutOfRange("bca"),
              mandiri: isGatewayOutOfRange("mandiri"),
              bri: isGatewayOutOfRange("bri"),
              bni: isGatewayOutOfRange("bni"),
              permata: isGatewayOutOfRange("permata"),
              bjb: isGatewayOutOfRange("bjb"),
              bsi: isGatewayOutOfRange("bsi"),
              cimb: isGatewayOutOfRange("cimb"),
              // PHP
              shopeepay: isGatewayOutOfRange("shopeepay"),
              grabpay: isGatewayOutOfRange("grabpay"),
              gcash: isGatewayOutOfRange("gcash"),
              paymaya: isGatewayOutOfRange("paymaya"),
              // JPY
              paypay: isGatewayOutOfRange("paypay"),
              alipay: isGatewayOutOfRange("alipay"),
              linepay: isGatewayOutOfRange("linepay"),
              rakutenpay: isGatewayOutOfRange("rakutenpay"),
              merpay: isGatewayOutOfRange("merpay"),
              inter_cc: isGatewayOutOfRange("inter_cc"),
              wechat_pay: isGatewayOutOfRange("wechat_pay"),
            };

            // availability asli dari backend (untuk show/hide)
            const backendEnabledPayments = {
              isDanaEnabled,
              isQRISEnabled,
              isGopayEnabled,
              isLinkAjaEnabled,
              isOvoEnabled,
              isBCAVAEnabled,
              isMandiriVAEnabled,
              isBRIVAEnabled,
              isBNIVAEnabled,
              isPermataVAEnabled,
              isBJBVAEnabled,
              isBSIVAEnabled,
              isCIMBVAEnabled,
            };

            const selectedGateway = values.payment_type || selectedPaymentMethod;
            const isSelectedPaymentAmountValid = selectedGateway
              ? !disabledPayments[selectedGateway]
              : true;

            const allowPayment =
              !Object.keys(errors).length &&
              values.agree &&
              isSelectedPaymentAmountValid;

            const handleCheckReward = async (amount = null, message = null) => {
              const amountToCheck = amount || values.amount;
              const messageToCheck = message || values.message;
              if (amountToCheck || messageToCheck) {
                const { username } = router.query;
                const isRewardEligible = await checkReward(username, { ...values, amount: amountToCheck, message: messageToCheck });
                setShowRewardNotification(isRewardEligible);
              }
            };

            return (
              <>
                <Form>
                  <Field type="number" name="amount">
                    {({ field, form }) => {
                      return (
                        <FormControl
                          isRequired
                          mb={3}
                          isInvalid={form.errors.amount && form.touched.amount}
                        >
                          <FormLabel htmlFor="amount">
                            <Trans>Nominal:</Trans>
                          </FormLabel>
                          <FormattedNumberInput
                            currency={currency}
                            mb={2}
                            data-testid="amount-input"
                            id="amount"
                            placeholder={_(msg`Ketik jumlah dukungan`)}
                            {...field}
                            onBlur={() => handleCheckReward(values.amount, values.message)}
                            onChange={() => {
                              setSelectedPaymentMethod();
                              // show amount errors while typing, not only on blur
                              form.setFieldTouched("amount", true, false);
                              // skip validation here: it would use the old amount and
                              // overwrite the check run by the input's own setValue
                              setFieldValue("payment_type", "", false);
                              setFieldValue("amountToPay", "", false);
                              setFieldValue("pgFee", "", false);
                              setFieldValue("platformFee", "", false);
                            }}
                            form={form}
                          />
                          <FormErrorMessage mb={2}>
                            {getFormError(form.errors.amount, _)}
                          </FormErrorMessage>
                          {/* <HStack>
                          <FormHelperText mb={2}>
                            <Trans>Minimum alert:</Trans>
                            <Text as="span" color="red.400">
                              {` ${formatMoney(minimumAlert, { currency })}`}
                            </Text>
                          </FormHelperText> */}
                          {/* Show minimum tts if tts is enabled */}
                          {/* {tts ? (
                            <FormHelperText mb={2}>
                              <Trans>TTS:</Trans>
                              <Text as="span" color="red.400">
                                {` ${formatMoney(minTTS, { currency })}`}
                              </Text>
                            </FormHelperText>
                          ) : null}
                        </HStack> */}

                          <QuickAmounts
                            mt={3}
                            mb={3}
                            quickAmounts={quickAmounts}
                            currAmount={values.amount}
                            setAmount={(amount) => {
                              // one update: separate setFieldValue calls validate
                              // against the old amount and keep its error shown
                              setValues((prev) => ({
                                ...prev,
                                amount,
                                amountToPay: "",
                                pgFee: "",
                                platformFee: "",
                                payment_type: "",
                              }));
                              setSelectedPaymentMethod();
                              setTimeout(() => {
                                if (amount || values.message) {
                                  handleCheckReward(amount, values.message);
                                }
                              }, 0);

                            }}
                            currency={currency}
                          />
                          {(values.amount && values.amount < minimumAlert) && (
                            <MinAmountInfo
                              labels={["Min. muncul alert:"]}
                              amounts={[minimumAlert]}
                              currency={currency}
                              reactIcon={FaBell}
                            />
                          )}

                        </FormControl>
                      );
                    }}
                  </Field>
                  {priorityAlert.isEnabled ? (
                    <PriorityAlertSelector
                      priorityAlert={priorityAlert}
                      currency={currency}
                      onToggle={() => {
                        setFieldValue("priority_alert", !priorityAlert.isSelected);
                        // amount to pay changes, payment method must be re-selected
                        setSelectedPaymentMethod();
                        setFieldValue("payment_type", "");
                        setFieldValue("amountToPay", "");
                        setFieldValue("pgFee", "");
                        setFieldValue("platformFee", "");
                      }}
                    />
                  ) : null}
                  <Field type="text" name="from">
                    {({ field, form }) => {
                      return (
                        <FormControl
                          isRequired
                          isReadOnly={currUsername}
                          mb={3}
                          mt={5}
                          isInvalid={form.errors.from && form.touched.from}
                        >
                          <FormLabel htmlFor="from">
                            <Trans>Dari:</Trans>
                          </FormLabel>
                          {currUsername ? (
                            <UsernameBadge
                              w={"min-content"}
                              username={currUsername}
                            ></UsernameBadge>
                          ) : (
                            <Input
                              {...field}
                              data-testid="from-input"
                              id="from"
                              placeholder={_(msg`Budi`)}
                            />
                          )}
                          <FormErrorMessage>
                            {getFormError(form.errors.from, _)}
                          </FormErrorMessage>
                        </FormControl>
                      );
                    }}
                  </Field>

                  <Field name="email">
                    {({ field, form }) => {
                      return (
                        <FormControl
                          display={currEmail ? "none" : "block"}
                          isRequired
                          isReadOnly={currEmail}
                          mb={3}
                          isInvalid={form.errors.email && form.touched.email}
                        >
                          <FormLabel htmlFor="email">
                            <Trans>Email:</Trans>
                          </FormLabel>
                          <Input
                            {...field}
                            data-testid="email-input"
                            type="email"
                            id="email"
                            placeholder={_(msg`budi@saweria.co`)}
                          />
                          <FormErrorMessage>
                            {getFormError(form.errors.email, _)}
                          </FormErrorMessage>
                        </FormControl>
                      );
                    }}
                  </Field>

                  <Field type="text" name="message">
                    {({ field, form }) => {
                      return (
                        <FormControl
                          isRequired
                          mb={3}
                          isInvalid={form.errors.message && form.touched.message}
                        >
                          <FormLabel htmlFor="message">
                            <Trans>Pesan:</Trans>
                          </FormLabel>
                          <Input
                            {...field}
                            data-testid="message-input"
                            id="message"
                            placeholder={_(msg`Selamat pagi`)}
                            onBlur={() => handleCheckReward(values.amount, values.message)}
                          />
                          <FormErrorMessage>
                            {getFormError(form.errors.message, _)}
                          </FormErrorMessage>
                          {(values.amount && values.amount < minTTS) && (
                            <MinAmountInfo
                              labels={["Min. Text To Speech:"]}
                              amounts={[minTTS]}
                              currency={currency}
                              reactIcon={PiSpeakerSimpleHighFill}
                            />
                          )}
                        </FormControl>
                      );
                    }}
                  </Field>
                  <ChallengeSelector
                    challenge={challenge}
                    currency={currency}
                    onToggle={(slug) => {
                      setFieldValue(
                        "challenge_slug",
                        challenge.selected?.slug === slug ? "" : slug
                      );
                    }}
                  />
                  {mabar ? (
                  <MabarSelector
                    mabarSetting={mabar}
                    values={values}
                    setFieldValue={setFieldValue}
                    currency={currency}
                    errors={errors}
                    disabled={Boolean(challenge.selected)}
                  />
                  ) : null}
                  {vote.on ? (
                    <VoteSelector
                      chooseVote={chooseVote}
                      title={vote.title}
                      tags={vote.tag}
                      selectedVote={values.vote}
                    />
                  ) : null}
                  <MediaSelector
                    currency={currency}
                    plus={plus}
                    overlay={overlay}
                    values={values}
                    setFieldValue={setFieldValue}
                    setFieldError={setFieldError}
                    errors={errors}
                    touched={touched}
                    disabled={!!values.mabar_type}
                    challengeSelected={Boolean(challenge.selected)}
                  />

                  {["ovo"].includes(values.payment_type) ? (
                    <>
                      <Field name="phone">
                        {({ field, form }) => {
                          return (
                            <FormControl
                              isRequired
                              mb={3}
                              isInvalid={form.errors.phone && form.touched.phone}
                            >
                              <FormLabel htmlFor="phone">
                                <Trans>Nomor telepon:</Trans>
                              </FormLabel>
                              <Input
                                {...field}
                                type="tel"
                                data-testid="phone-input"
                                id="phone"
                                placeholder={_(
                                  msg`Nomor yang terdaftar di e-wallet pilihan`
                                )}
                              />
                              <FormErrorMessage>
                                {getFormError(form.errors.phone, _)}
                              </FormErrorMessage>
                            </FormControl>
                          );
                        }}
                      </Field>
                    </>
                  ) : null}
                  <Field name="notUnderage">
                    {({ field, form }) => {
                      return (
                        <FormControl
                          isRequired
                          mb="3"
                          isInvalid={
                            form.errors.notUnderage && form.touched.notUnderage
                          }
                        >
                          <Checkbox
                            data-testid="underage-checkbox"
                            size="lg"
                            {...field}
                            isInvalid={
                              form.errors.notUnderage && form.touched.notUnderage
                            }
                            borderColor="black"
                            isChecked={form.values.notUnderage}
                          >
                            <P fontSize="0.75em" mb={0}>
                              <Trans>Saya berusia 17 tahun atau lebih</Trans>
                            </P>
                          </Checkbox>

                          <FormErrorMessage>
                            {getFormError(form.errors.notUnderage, _)}
                          </FormErrorMessage>
                        </FormControl>
                      );
                    }}
                  </Field>

                  <Field name="agree">
                    {({ field, form }) => {
                      return (
                        <FormControl
                          isRequired
                          mb="3"
                          isInvalid={form.errors.agree && form.touched.agree}
                        >
                          <Checkbox
                            data-testid="agree-checkbox"
                            size="lg"
                            {...field}
                            isInvalid={form.errors.agree && form.touched.agree}
                            borderColor="black"
                            isChecked={form.values.agree}
                          >
                            <P fontSize="0.75em" mb={0}>
                              <Trans>
                                Saya memahami dan menyetujui bahwa dukungan ini
                                saya berikan secara sukarela untuk mendukung
                                kreator, tidak dapat dikembalikan, bukan transaksi
                                komersial, tidak digunakan untuk aktivitas ilegal,
                                serta telah sesuai dengan{" "}
                                <Link textDecoration="underline" href="/terms">
                                  {`syarat dan ketentuan`}
                                </Link>{" "}
                                &{" "}
                                <Link textDecoration="underline" href="/privacy">
                                  kebijakan privasi
                                </Link>{" "}
                                saweria.co
                              </Trans>
                            </P>
                          </Checkbox>

                          <FormErrorMessage>
                            {getFormError(form.errors.agree, _)}
                          </FormErrorMessage>
                        </FormControl>
                      );
                    }}
                  </Field>
                  <Large fontSize={["lg", "xl"]}>
                    <Trans>Moda pembayaran:</Trans>
                  </Large>
                  <PaymentMethods
                    currency={currency}
                    isSubmitting={isSubmitting}
                    allowPayment={allowPayment}
                    enabledPayments={backendEnabledPayments}
                    disabledPayments={disabledPayments}
                    donate={onClickPaymentMethod}
                  />
                  <Flex justifyContent="space-between" marginTop={6} >
                    <Flex flexDirection="column">
                      <Large fontSize={["md"]}>
                        <Trans>Jumlah Dukungan: </Trans>{" "}
                        {formatMoney(
                          values.amount === "" ? values.amount : totalAmount,
                          { currency }
                        )}
                      </Large>
                      {(values.platformFee > 0) ? (

                        <Large fontSize={["sm"]}>
                          <Trans>Biaya Layanan: </Trans>{" "}
                          {formatMoney(values.platformFee ? values.platformFee : 0, { currency })}
                        </Large>
                      ) : null}
                      {transactionFeePolicy === "TIPPER" && (
                        <>
                          <Large fontSize={["sm"]}>
                            <Trans>Biaya Pembayaran (Payment Gateway):</Trans>{" "}
                            {formatMoney(
                              values.pgFee ? values.pgFee : 0,
                              {
                                currency,
                              }
                            )}
                          </Large>
                        </>
                      )}
                      <Large>
                        <Trans>Total:</Trans>{" "}
                        {formatMoney(values.amountToPay, { currency })}
                      </Large>
                      <Text color="red.500" fontSize="md">
                        {getFormError(errors.payment_type, _)}
                      </Text>
                    </Flex>
                    <Flex>
                      <Button
                        marginBottom={0}
                        disabled={!allowPayment || !selectedPaymentMethod}
                        data-testid="donate-button"
                        backgroundColor={`saweria-yellow.${
                          allowPayment && selectedPaymentMethod ? "normal" : "dark"
                        }`}
                        type="button"
                        label={t`Kirim Dukungan`}
                        onClick={submitForm}
                      />
                    </Flex>
                  </Flex>
                  <Flex ref={donateBtnRef} />
                </Form>
                {showRewardNotification && (
                  <Flex
                    position="fixed"
                    bottom={0}
                    left="50%"
                    transform="translateX(-50%)"
                    bg="#DCEEFF"
                    color="#3182CE"
                    p={3}
                    align="center"
                    justify="center"
                    zIndex={1000}
                    boxShadow="0 -2px 10px rgba(0,0,0,0.1)"
                    border="1px solid #3182CE"
                    borderRadius="8px 8px 0 0"
                    maxW="700px"
                    w="100%"
                    display={isRewardHidden ? "none" : "flex"}
                  >
                    <Text fontSize="md" fontWeight="700" textAlign="center">
                      🎁 <Trans>Kirim dukungan & dapatkan kiriman spesial!</Trans>
                    </Text>
                  </Flex>

                )}

              </>
            );
          }}
        </Formik>
      </PaymentMethodSelectorProvider>
    </GiphyContextProvider>
  );
};

const QuickAmounts = ({ quickAmounts, currAmount, setAmount, currency, ...props }) => {
  const amountButtonHelper = quickAmounts
    .sort((a, b) => a - b)
    .map((amount, index) => {
      const label =
        currency === "JPY"
          ? `${formatMoney(amount, { currency })}`
          : shortHandNumber(amount);
      return (
        <WrapItem key={`button-helper-${amount}-${index}`} flexGrow={1}>
          <Button
            {...(currAmount == amount ? { boxShadow: "pressed" } : null)}
            transition={"box-shadow .1s , transform .2s "}
            type="button"
            disabled={currAmount == amount}
            onClick={(e) => {
              e.preventDefault();
              setAmount(amount);
            }}
            w="100%"
            m={0}
            label={label}
            bgColor={quickAmountColors[index % 4]}
          ></Button>
        </WrapItem>
      );
    });
  return <Wrap {...props}>{amountButtonHelper}</Wrap>;
};

export { DonationForm, QuickAmounts, getPriorityAlert, getChallenge };
